import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getPopularPublicDecks, Deck, isDeckInUserLibrary, addPublicDeckToUser } from '../services/deckService';
import { getUserInfo } from '../services/firebase';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import LayersIcon from '@mui/icons-material/Layers';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [popularDecks, setPopularDecks] = useState<Deck[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deckStatuses, setDeckStatuses] = useState<Record<string, {isOwn: boolean, isInLibrary: boolean}>>({});
  const [deckAuthors, setDeckAuthors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const fetchPopularDecks = async () => {
      setIsLoading(true);
      try {
        const decks = await getPopularPublicDecks(6);
        setPopularDecks(decks);
        
        // Получаем информацию об авторах колод
        const authors: Record<string, string> = {};
        for (const deck of decks) {
          if (!authors[deck.userId]) {
            const userInfo = await getUserInfo(deck.userId);
            authors[deck.userId] = userInfo?.displayName || t('decks.unknownUser');
          }
        }
        setDeckAuthors(authors);
        
        if (user) {
          // Проверяем статус каждой колоды для пользователя
          const statuses: Record<string, {isOwn: boolean, isInLibrary: boolean}> = {};
          for (const deck of decks) {
            const isOwn = deck.userId === user.uid;
            const isInLibrary = isOwn ? true : await isDeckInUserLibrary(user.uid, deck.id);
            statuses[deck.id] = { isOwn, isInLibrary };
          }
          setDeckStatuses(statuses);
        }
      } catch (error) {
        console.error('Error fetching popular decks:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPopularDecks();
  }, [user, t]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/public-decks?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleAddDeck = async (deckId: string, deckTitle: string) => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    // Проверяем, не является ли колода собственной или уже добавленной
    const status = deckStatuses[deckId] || { isOwn: false, isInLibrary: false };
    if (status.isOwn) {
      setSnackbar({
        open: true,
        message: t('decks.ownDeckMessage'),
        severity: 'info',
      });
      return;
    }
    
    if (status.isInLibrary) {
      setSnackbar({
        open: true,
        message: t('decks.alreadyInLibraryMessage'),
        severity: 'info',
      });
      return;
    }
    
    try {
      const success = await addPublicDeckToUser(user.uid, deckId);
      if (success) {
        // Обновляем статус колоды
        setDeckStatuses(prev => ({
          ...prev,
          [deckId]: { ...prev[deckId], isInLibrary: true }
        }));
        
        // Обновляем локальное значение популярности
        setPopularDecks(prevDecks => 
          prevDecks.map(deck => {
            if (deck.id === deckId) {
              return {
                ...deck,
                stats: {
                  ...deck.stats,
                  popularity: (deck.stats.popularity || 0) + 1
                }
              };
            }
            return deck;
          })
        );
        
        setSnackbar({
          open: true,
          message: t('messages.deckAddedToLibrary'),
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: t('messages.failedToAddDeck'),
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error adding deck to library:', error);
      setSnackbar({
        open: true,
        message: t('messages.errorAddingDeck'),
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 8 }}>
        <Box sx={{ 
          textAlign: 'center',
          mb: 8,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -16,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '200px',
            height: '2px',
            background: 'linear-gradient(90deg, #EF233C, transparent)',
          }
        }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              mb: 2,
              fontFamily: '"Noto Serif JP", serif',
              fontWeight: 500,
            }}
          >
            {t('app.title')}
          </Typography>
          <Typography 
            variant="h5" 
            color="text.secondary" 
            sx={{ maxWidth: '600px', mx: 'auto' }}
          >
            {t('app.slogan')}
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(3, 1fr)'
          },
          gap: 4,
          mb: 8
        }}>
          <Paper sx={{ 
            p: 3, 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
            }
          }}>
            <SchoolIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('home.feature1Title')}
            </Typography>
            <Typography color="text.secondary" paragraph>
              {t('home.feature1Desc')}
            </Typography>
          </Paper>

          <Paper sx={{ 
            p: 3, 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
            }
          }}>
            <LibraryBooksIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('home.feature2Title')}
            </Typography>
            <Typography color="text.secondary" paragraph>
              {t('home.feature2Desc')}
            </Typography>
          </Paper>

          <Paper sx={{ 
            p: 3, 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
            }
          }}>
            <GroupIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('home.feature3Title')}
            </Typography>
            <Typography color="text.secondary" paragraph>
              {t('home.feature3Desc')}
            </Typography>
          </Paper>
        </Box>

        <Paper sx={{ p: 4, mb: 6 }}>
          <form onSubmit={handleSearch}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
              {t('home.findPublicDecks')}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('decks.searchDecksPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button 
                      type="submit"
                      variant="contained"
                    >
                      {t('common.search')}
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button onClick={() => navigate('/public-decks')}>
                {t('home.browseAllPublicDecks')}
              </Button>
            </Box>
          </form>
        </Paper>

        <Box sx={{ mb: 8 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            gap: 2
          }}>
            <EmojiEventsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4">
              {t('decks.popularDecks')}
            </Typography>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : popularDecks.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography>{t('decks.noPopularDecks')}</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {popularDecks.map((deck) => {
                const status = deckStatuses[deck.id] || { isOwn: false, isInLibrary: false };
                const authorName = status.isOwn ? t('decks.createdByYou') : deckAuthors[deck.userId] || t('decks.unknownUser');
                
                return (
                  <Box key={deck.id} sx={{ width: { xs: '100%', sm: '45%', md: '31%' } }}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6,
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="div" gutterBottom>
                          {deck.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {authorName}
                          </Typography>
                        </Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {deck.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title={t('decks.cardCount')} arrow>
                              <LayersIcon fontSize="small" color="action" />
                            </Tooltip>
                            <Typography variant="body2" color="text.secondary">
                              {deck.stats.totalCards}
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5, 
                            ml: 'auto', 
                            color: 'gold' 
                          }}>
                            <StarIcon fontSize="small" />
                            <Typography variant="body2">
                              {deck.stats.popularity || 0}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {deck.tags.slice(0, 3).map((tag) => (
                            <Chip key={tag} label={tag} size="small" />
                          ))}
                          {deck.tags.length > 3 && (
                            <Chip label={t('home.moreTags', { count: deck.tags.length - 3 })} size="small" variant="outlined" />
                          )}
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/decks/${deck.id}`)}
                        >
                          {t('common.view')}
                        </Button>
                        {user ? (
                          status.isOwn ? (
                            <Button 
                              size="small"
                              color="primary"
                              startIcon={<PersonIcon />}
                              onClick={() => navigate('/decks')}
                            >
                              {t('decks.yourDeck')}
                            </Button>
                          ) : status.isInLibrary ? (
                            <Button 
                              size="small"
                              color="success"
                              startIcon={<CheckIcon />}
                              onClick={() => navigate('/decks')}
                            >
                              {t('decks.inLibrary')}
                            </Button>
                          ) : (
                            <Button 
                              size="small"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddDeck(deck.id, deck.title)}
                            >
                              {t('decks.addToLibrary')}
                            </Button>
                          )
                        ) : (
                          <Button 
                            size="small"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddDeck(deck.id, deck.title)}
                          >
                            {t('decks.signInToAddButton')}
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default HomePage; 